import { faXmark } from '@fortawesome/pro-light-svg-icons'
import { roundNumberAndDecimals } from '@teinor/erp/prices'
import { totalCalculateParams } from '@teinor/erp/prices/quote/calculate'
import { VoucherModelType } from '@teinor/erp/types/company/accounting/voucher'
import { useCallback } from 'react'
import { flushSync } from 'react-dom'
import TwinIcon from '../../../../../../../../baseComponents/TwinIcon'
import ITHeaderText from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { CustomSelectSearchBarWithNotInFilteredItems } from '../../../../../../../../forms/CustomSelect'
import { InputWithLabelMargin } from '../../../../../../../../forms/Input'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { getCurrencySymbol } from '../../../../../../../../utils/globals/price'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { getISOCurrencyMain } from '../../../../../../../../utils/reducers/getters'
import { changeErrorMessage } from '../../../../../../../../utils/reducers/reduxDispatch'
import CustomInputTotalsTablePriceTPV from '../../../CustomInputTotalsTable'
import { TPVSetTotalPaymentsCompleteTy, TPVTotalPaymentsCompleteTy, VoucherData } from '../../types'

interface VoucherComponentTPVProps extends VoucherComponentTPVLogicProps {
    customChangeField?: (index: number, field: keyof VoucherData, value: any) => void
    isHeader?: boolean
    PaymentTypeId: number
}

const VoucherComponentTPV: React.FC<VoucherComponentTPVProps> = ({ items, myTotalPayments, isHeader, PaymentTypeId, ...rest}) => {
    const { t } = useTwinTranslation()
    const { searchSpecificVoucher, handleChangeField, handleDeleteVoucherRow } = useVoucherComponentTPVLogic({ items, myTotalPayments, ...rest })
    const renderThis: JSX.Element[] = []
    const CurrencyId = getISOCurrencyMain()
    for (const key in myTotalPayments.voucherData?.vouchers) {
        const element = myTotalPayments.voucherData?.vouchers?.[parseInt(key)]
        if (element) {
            renderThis.push(<VoucherRow voucherData={element} key={key} index={parseInt(key)} handleDeleteVoucherRow={handleDeleteVoucherRow} />)
        }
    }
    const lessVouchersThanTotal = ((myTotalPayments.voucherData?.totalVouchers || 0) < myTotalPayments.totalsByTypes.byPaymentType[PaymentTypeId])
    if (lessVouchersThanTotal) {
        renderThis.push(<SelectVoucherRow handleChangeField={handleChangeField} items={items} searchSpecificVoucher={searchSpecificVoucher} key={'select'} selectedVouchers={myTotalPayments?.voucherData?.selectedVouchers || []} index={myTotalPayments.voucherData?.vouchers?.length ? myTotalPayments.voucherData?.vouchers?.length : 0} />)
    }
    return (
        <div className='voucher_parent flex flex-auto flex-col gap-y-10'>
            {renderThis}
            {!isHeader ? <CustomInputTotalsTablePriceTPV currency={CurrencyId || 'EUR'} inputName={t('totalVouchers', 'Total cupones') + ':'} value={myTotalPayments.voucherData?.totalVouchers || 0} className='regular14' classNameValue='regular16' /> : null}
        </div>
    )
}

interface VoucherComponentTPVLogicProps {
    items: TwinDictionary
    setMyTotalPayments: TPVSetTotalPaymentsCompleteTy
    myTotalPayments: TPVTotalPaymentsCompleteTy
    onChangeField?: (maxValue?: number) => void
  
}

const useVoucherComponentTPVLogic = ({ items, setMyTotalPayments, myTotalPayments, onChangeField }: VoucherComponentTPVLogicProps) => {
    const { t } = useTwinTranslation()
    const notValidVoucherMsg = t('ThisVoucherIsNotValid', 'Este número de vale no es válido')
    const addVoucher = useCallback((index: number, voucher: TwinDictionary) => {
        let myMaxValue = 0
        flushSync(async () => {
            await setMyTotalPayments((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                if (!copyOld['voucherData']?.['vouchers']?.[index]) {
                    copyOld['voucherData']['vouchers'][index] = {}
                }
                    copyOld['voucherData']['vouchers'][index]['id'] = voucher?.id
                    copyOld['voucherData']['vouchers'][index]['voucherNumber'] = voucher?.voucher_n
                    copyOld['voucherData']['vouchers'][index]['quantity'] = voucher?.amount
                    copyOld['voucherData']['vouchers'][index]['CurrencyId'] = voucher?.subType
                    copyOld['voucherData']['totalVouchers'] = roundNumberAndDecimals(copyOld['voucherData']['totalVouchers'] + parseFloat(voucher?.amount), totalCalculateParams)
                    myMaxValue = copyOld['voucherData']['totalVouchers']
                copyOld['voucherData']['selectedVouchers'].push(voucher?.id)
                return copyOld
            })
            if (myMaxValue) {
                onChangeField?.(myMaxValue)
            }
        })
    }, [setMyTotalPayments, onChangeField])

    const handleChangeField = useCallback((index: number, value: any) => {
        const myVoucher = items[value]
        if (myVoucher) {
            addVoucher(index, items[value])
        }
    }, [items, addVoucher])


    const handleDeleteVoucherRow = useCallback((index: number) => {
        flushSync(async () => {
            let myMaxValue = 0
            await setMyTotalPayments((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                const amount = copyOld['voucherData']['vouchers'][index].quantity
                delete copyOld['voucherData']['vouchers'][index]
                copyOld['voucherData']['totalVouchers'] = roundNumberAndDecimals(copyOld['voucherData']['totalVouchers'] - parseFloat(amount), totalCalculateParams)
                const newVoucherData = []
                const selectedVouchers = []
                for (const element of copyOld['voucherData']['vouchers']) {
                    if (element && Object.keys(element).length) {
                        newVoucherData.push(element)
                        if (element?.id) {
                            selectedVouchers.push(element?.id)
                        }
                    }
                }
                copyOld['voucherData']['vouchers'] = newVoucherData
                copyOld['voucherData']['selectedVouchers'] = selectedVouchers
                myMaxValue = copyOld['voucherData']['totalVouchers']
                return copyOld
            })
            onChangeField?.(myMaxValue)
        })

    }, [setMyTotalPayments, onChangeField])

    const searchSpecificVoucher = useCallback(async (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            let result: VoucherModelType | null = null
            const myVoucherN = event.currentTarget.value
            result = await twinFetchPostJSON('/api/app/order/tpv/sellTicket/getVoucherByVoucherNumber', {
                voucher_n: myVoucherN.trim(),
                ids: myTotalPayments?.['voucherData']?.selectedVouchers
            })
            if (result) {
                addVoucher(index, result)
            } else {
                changeErrorMessage(notValidVoucherMsg)
            }
        }
    }, [myTotalPayments, notValidVoucherMsg, addVoucher])

    return { searchSpecificVoucher, handleChangeField, handleDeleteVoucherRow }
}

interface VoucherRowProps {
    voucherData: VoucherData
    handleDeleteVoucherRow: (index: number) => void
    index: number
}

const VoucherRow: React.FC<VoucherRowProps> = ({ voucherData, index, handleDeleteVoucherRow }) => {
    const currency = getCurrencySymbol(voucherData?.CurrencyId || '')
    const label = (voucherData?.voucherNumber || '') + ' (' + voucherData.quantity + currency + ')'
    return (
        <div className='voucher_tpv_readonly flex items-center'>
            <div className='input_table_new_whitebox flex flex-auto '>
                <ITHeaderText label='' value={label} className=' '/>
            </div>
            <div onClick={() => handleDeleteVoucherRow(index)} className='cursor-pointer w-28 h-28 flex justify-center items-center'><TwinIcon icon={faXmark} /></div>
        </div>
    )
}
interface SelectVoucherRowProps {
    items: TwinDictionary
    handleChangeField: (index: number, value: any) => void
    searchSpecificVoucher: (index: number, event: React.KeyboardEvent<HTMLInputElement>) => Promise<void>
    index: number
    selectedVouchers: string[]
}

const SelectVoucherRow: React.FC<SelectVoucherRowProps> = ({ items, handleChangeField, searchSpecificVoucher, index, selectedVouchers }) => {
    const { t } = useTwinTranslation()
    return (
        <div className='voucher_tpv flex flex-auto justify-end'>
            {items && Object.keys(items).length ? <CustomSelectSearchBarWithNotInFilteredItems className='flex flex-auto  custom_select_hide_label h-28  overflow-visible mr-20' items={items} label='' onChange={(value) => handleChangeField(index, value)} fieldName='label'  excludeItems={selectedVouchers} placeholder={t('selectVoucher', 'Seleccionar vale')} /> : null}
            <InputWithLabelMargin className='ticket_number_selector flex flex-auto ' label={''} onKeyDown={(event) => searchSpecificVoucher(index, event)} placeholder={t('voucherNumber', 'Nº de vale')} hideLabel={true} />
        </div>
    )
}


export default VoucherComponentTPV